'use client';

import { useState, useRef, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { Logo } from '@/components/layout/logo';
import type { MembershipCardField, CardTemplate } from '@/models/membership-card-config';
import { getFieldValue } from '@/models/membership-card-config';

interface MembershipCardPreviewProps {
  fields: MembershipCardField[];
  template: CardTemplate;
  templateType: 'card' | 'sticker';
  membershipData?: any;
  profileData?: any;
  sharedData?: Record<string, any>;
  userData?: any;
  onFieldUpdate?: (fieldId: string, updates: Partial<MembershipCardField>) => void;
  onFieldClick?: (field: MembershipCardField) => void;
  selectedFieldId?: string | null;
  interactive?: boolean;
}

interface CardFieldDisplayProps {
  field: MembershipCardField;
  value: string | undefined;
  primaryColor: string;
  secondaryColor: string;
  templateType: 'card' | 'sticker';
  interactive: boolean;
  selected?: boolean;
  onUpdate?: (fieldId: string, updates: Partial<MembershipCardField>) => void;
  onClick?: (field: MembershipCardField) => void;
  onDragStateChange?: (isDragging: boolean) => void;
}

// Sample data for preview
const SAMPLE_MEMBERSHIP_DATA = {
  id: 'SRAC-2024-001',
  firstName: 'John',
  lastName: 'Doe',
  email: 'john.doe@example.com',
  phone: '(555) 123-4567',
  status: 'Active',
  flightLevel: 'Advanced',
  amaMembershipId: '123456',
  renewalDate: '2024-12-31',
  joinDate: '2020-01-15',
  category: 'Open Member'
};

const SAMPLE_USER_DATA = {
  displayName: 'John Doe',
  email: 'john.doe@example.com',
  photoURL: '',
  phoneNumber: '(555) 123-4567'
};

export default function MembershipCardPreview({
  fields,
  template,
  templateType,
  membershipData = SAMPLE_MEMBERSHIP_DATA,
  profileData,
  sharedData,
  userData = SAMPLE_USER_DATA,
  onFieldUpdate,
  onFieldClick,
  selectedFieldId,
  interactive = false
}: MembershipCardPreviewProps) {
  const [isDraggingAny, setIsDraggingAny] = useState(false);

  // Filter fields based on template type
  const visibleFields = fields
    .filter(f => templateType === 'card' ? f.showOnCard : f.showOnSticker)
    .filter(f => f.type !== 'qrcode') // Don't show QR code in preview
    .sort((a, b) => a.order - b.order);

  const { width, height, unit } = template.dimensions;
  const { backgroundColor, borderColor, borderWidth, borderRadius, primaryColor, secondaryColor } = template.styling;
  const showGridLines = template.layout.showGridLines || isDraggingAny;
  const gridColumns = template.layout.gridColumns || 4;
  const gridRows = template.layout.gridRows || 6;
  const padding = template.layout.padding || 24;

  // Calculate grid cell dimensions based on available space
  const containerWidth = unit === 'px' ? width : width;
  const containerHeight = unit === 'px' ? height : height;
  const gridAreaWidth = containerWidth - (padding * 2);
  const gridAreaHeight = containerHeight - (padding * 2);
  const cellWidth = gridAreaWidth / gridColumns;
  const cellHeight = gridAreaHeight / gridRows;

  const handleDragStateChange = (isDragging: boolean) => {
    setIsDraggingAny(isDragging);
  };

  return (
    <div className="w-full flex items-center justify-center p-4 bg-muted/30 rounded-lg">
      <div
        data-card-element="true"
        className="relative shadow-lg"
        style={{
          width: unit === 'px' ? `${width}px` : `${width}${unit}`,
          height: unit === 'px' ? `${height}px` : `${height}${unit}`,
          backgroundColor,
          borderColor,
          borderWidth: `${borderWidth}px`,
          borderRadius: `${borderRadius}px`,
          borderStyle: 'solid',
        }}
      >
        {/* Fields - Grid Layout with optional grid lines */}
        <div
          className="h-full relative"
          style={{
            padding: `${padding}px`,
            userSelect: interactive ? 'none' : 'auto',
            WebkitUserSelect: interactive ? 'none' : 'auto',
          }}
        >
          <div
            className="h-full w-full"
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
              gridTemplateRows: `repeat(${gridRows}, 1fr)`,
              gap: '0.75rem',
              userSelect: interactive ? 'none' : 'auto',
              WebkitUserSelect: interactive ? 'none' : 'auto',
              ...(showGridLines && {
                backgroundImage: `
                  linear-gradient(to right, rgba(0,0,0,0.1) 1px, transparent 1px),
                  linear-gradient(to bottom, rgba(0,0,0,0.1) 1px, transparent 1px)
                `,
                backgroundSize: `${cellWidth}px ${cellHeight}px`,
                backgroundPosition: '0 0',
              })
            }}
          >
          {visibleFields.map(field => (
            <CardFieldDisplay
              key={field.id}
              field={field}
              value={getFieldValue(membershipData, field, sharedData, userData, profileData)}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              templateType={templateType}
              interactive={interactive}
              selected={selectedFieldId === field.id}
              onUpdate={onFieldUpdate}
              onClick={onFieldClick}
              onDragStateChange={handleDragStateChange}
            />
          ))}
          </div>
        </div>

        {/* Watermark for preview */}
        <div 
          className="absolute bottom-2 right-2 text-xs opacity-30 z-10"
          style={{ color: secondaryColor }}
        >
          Preview
        </div>
      </div>
    </div>
  );
}

function CardFieldDisplay({
  field,
  value,
  primaryColor,
  secondaryColor,
  templateType,
  interactive,
  selected = false,
  onUpdate,
  onClick,
  onDragStateChange
}: CardFieldDisplayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [fontSize, setFontSize] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  // Get the appropriate grid position based on template type
  const gridPosition = templateType === 'card' ? field.gridPositionCard : field.gridPositionSticker;

  // Auto-scale font size to fit container
  useEffect(() => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const valueElement = container.querySelector('.field-value') as HTMLElement;
    if (!valueElement || field.type === 'logo') return;

    // Give browser time to render
    setTimeout(() => {
      const baseFontSize = parseFloat(field.styles?.fontSize || '0.875rem');
      const containerHeight = container.clientHeight;
      const containerWidth = container.clientWidth;
      const valueHeight = valueElement.scrollHeight;
      const valueWidth = valueElement.scrollWidth;

      let scaleFactor = 1;

      // Check if content overflows vertically or horizontally
      if (valueHeight > containerHeight * 0.85 || valueWidth > containerWidth) {
        const heightScale = (containerHeight * 0.85) / valueHeight;
        const widthScale = containerWidth / valueWidth;
        scaleFactor = Math.min(heightScale, widthScale, 1); // Use smallest scale, but don't scale up
      }

      if (scaleFactor < 1) {
        setFontSize(`${baseFontSize * scaleFactor}rem`);
      } else {
        setFontSize(field.styles?.fontSize || '0.875rem');
      }
    }, 50);
  }, [value, field.styles?.fontSize, gridPosition, field.type]);

  const renderValue = () => {
    switch (field.type) {
      case 'logo':
        return (
          <div className="flex items-center justify-center h-full">
            <Logo
              className="w-full h-full max-h-16"
              style={{ color: primaryColor }}
            />
          </div>
        );

      case 'date':
        if (!value) return <span className="italic opacity-50">Not set</span>;
        try {
          const date = new Date(value);
          return format(date, field.format || 'MM/dd/yyyy');
        } catch {
          return value;
        }

      case 'badge':
        if (!value) return <span className="italic opacity-50">Not set</span>;
        return (
          <Badge
            variant={value === 'Active' ? 'default' : 'secondary'}
            className="text-xs"
            style={{
              backgroundColor: value === 'Active' ? primaryColor : undefined
            }}
          >
            {value}
          </Badge>
        );

      default:
        if (!value) return <span className="italic opacity-50">Not set</span>;
        return value;
    }
  };

  const gridStyle = gridPosition ? {
    gridColumn: `${gridPosition.column} / span ${gridPosition.columnSpan}`,
    gridRow: `${gridPosition.row} / span ${gridPosition.rowSpan}`,
  } : {};

  const horizontalAlign = field.styles?.horizontalAlignment || 'left';
  const verticalAlign = field.styles?.verticalAlignment || 'top';
  const showLabel = templateType === 'card'
    ? (field.showLabelOnCard !== false)
    : (field.showLabelOnSticker !== false);
  
  // Map vertical alignment to flexbox values
  const alignItemsMap = {
    top: 'flex-start',
    middle: 'center',
    bottom: 'flex-end'
  };

  // Handle click
  const handleClick = (e: React.MouseEvent) => {
    if (!interactive || !onClick) return;
    e.stopPropagation();
    onClick(field);
  };

  // Handle drag start (for drag handle)
  const handleDragStart = (e: React.MouseEvent) => {
    if (!interactive || !onUpdate || !selected || !gridPosition) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    if (onDragStateChange) onDragStateChange(true);
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startCol = gridPosition.column;
    const startRow = gridPosition.row;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!containerRef.current) return;
      
      const container = containerRef.current.closest('[style*="display: grid"]') as HTMLElement;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const gridColumns = parseInt(getComputedStyle(container).gridTemplateColumns.split(' ').length.toString());
      const gridRows = parseInt(getComputedStyle(container).gridTemplateRows.split(' ').length.toString());
      
      const colWidth = rect.width / gridColumns;
      const rowHeight = rect.height / gridRows;
      
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      
      const colDelta = Math.round(deltaX / colWidth);
      const rowDelta = Math.round(deltaY / rowHeight);
      
      const newCol = Math.max(1, Math.min(gridColumns, startCol + colDelta));
      const newRow = Math.max(1, Math.min(gridRows, startRow + rowDelta));
      
      if (newCol !== startCol || newRow !== startRow) {
        // Update the correct grid position based on template type
        const gridPositionKey = templateType === 'card' ? 'gridPositionCard' : 'gridPositionSticker';
        onUpdate(field.id, {
          [gridPositionKey]: {
            column: newCol,
            row: newRow,
            columnSpan: gridPosition.columnSpan,
            rowSpan: gridPosition.rowSpan
          }
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (onDragStateChange) onDragStateChange(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Handle resize
  const handleResizeStart = (e: React.MouseEvent, direction: 'width' | 'height' | 'both') => {
    if (!interactive || !onUpdate || !selected || !gridPosition) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startColSpan = gridPosition.columnSpan;
    const startRowSpan = gridPosition.rowSpan;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!containerRef.current) return;
      
      const container = containerRef.current.closest('[style*="display: grid"]') as HTMLElement;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const gridColumns = parseInt(getComputedStyle(container).gridTemplateColumns.split(' ').length.toString());
      const gridRows = parseInt(getComputedStyle(container).gridTemplateRows.split(' ').length.toString());
      
      const colWidth = rect.width / gridColumns;
      const rowHeight = rect.height / gridRows;
      
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      
      let newColSpan = startColSpan;
      let newRowSpan = startRowSpan;
      
      if (direction === 'width' || direction === 'both') {
        const colDelta = Math.round(deltaX / colWidth);
        newColSpan = Math.max(1, Math.min(gridColumns - gridPosition.column + 1, startColSpan + colDelta));
      }
      
      if (direction === 'height' || direction === 'both') {
        const rowDelta = Math.round(deltaY / rowHeight);
        newRowSpan = Math.max(1, Math.min(gridRows - gridPosition.row + 1, startRowSpan + rowDelta));
      }
      
      if (newColSpan !== startColSpan || newRowSpan !== startRowSpan) {
        // Update the correct grid position based on template type
        const gridPositionKey = templateType === 'card' ? 'gridPositionCard' : 'gridPositionSticker';
        onUpdate(field.id, {
          [gridPositionKey]: {
            column: gridPosition.column,
            row: gridPosition.row,
            columnSpan: newColSpan,
            rowSpan: newRowSpan
          }
        });
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "card-field relative overflow-hidden flex flex-col transition-all",
        interactive && "cursor-pointer hover:ring-2 hover:ring-primary/50",
        selected && "ring-2 ring-primary shadow-md",
        isDragging && "ring-2 ring-primary opacity-50 cursor-move",
        isResizing && "ring-2 ring-blue-500"
      )}
      style={{
        textAlign: horizontalAlign,
        justifyContent: alignItemsMap[verticalAlign],
        ...gridStyle,
        minHeight: '2rem'
      }}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={interactive && isHovered ? field.label : undefined}
    >
      {/* Drag Handle (only when selected) */}
      {interactive && selected && onUpdate && !isDragging && !isResizing && (
        <div
          className="absolute top-1 left-1 p-1 bg-primary text-primary-foreground rounded cursor-move hover:bg-primary/90 z-20"
          onMouseDown={handleDragStart}
          title="Drag to reposition"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="5" y1="9" x2="19" y2="9" />
            <line x1="5" y1="15" x2="19" y2="15" />
          </svg>
        </div>
      )}
      {field.type === 'logo' ? (
        // Logo field - render logo directly without label
        renderValue()
      ) : (
        // Regular fields - show label and value
        <>
          {showLabel && (
            <div
              className="text-xs mb-0.5 leading-tight"
              style={{
                color: secondaryColor,
                fontSize: fontSize ? `calc(${fontSize} * 0.7)` : field.styles?.fontSize ? `calc(${field.styles.fontSize} * 0.7)` : '0.6rem',
              }}
            >
              {field.label}
            </div>
          )}
          <div
            className="font-semibold field-value leading-tight"
            style={{
              color: primaryColor,
              fontSize: fontSize || field.styles?.fontSize || '0.875rem',
              fontWeight: field.styles?.fontWeight || '600',
            }}
          >
            {renderValue()}
          </div>
        </>
      )}
      
      {/* Resize handles (only in interactive mode when selected) */}
      {interactive && selected && onUpdate && (
        <>
          {/* Right edge resize */}
          <div
            className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-blue-500/30"
            onMouseDown={(e) => handleResizeStart(e, 'width')}
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Bottom edge resize */}
          <div
            className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-blue-500/30"
            onMouseDown={(e) => handleResizeStart(e, 'height')}
            onClick={(e) => e.stopPropagation()}
          />
          
          {/* Bottom-right corner resize */}
          <div
            className="absolute bottom-0 right-0 w-3 h-3 cursor-nwse-resize hover:bg-blue-500 bg-blue-500/50 rounded-tl"
            onMouseDown={(e) => handleResizeStart(e, 'both')}
            onClick={(e) => e.stopPropagation()}
          />
        </>
      )}
    </div>
  );
}
